/* Изменение размера модальных окон мышью.
 *
 * Родное CSS `resize` здесь не годится ни в одном из двух случаев. Карточка
 * прижата к правому краю экрана, и тянуть её надо за ЛЕВУЮ границу — левую
 * сторону `resize` не умеет вовсе. Диалог отцентрован, а родная ручка растит
 * окно вправо-вниз: центр уезжает, и окно ползёт по экрану, пока его тянешь.
 *
 * Отсюда свои ручки. У отцентрованного окна прирост курсора удваивается
 * (`factor = 2`): обе стороны расходятся от центра, и без удвоения край
 * отстаёт от мыши вдвое.
 *
 * Размер переживает закрытие: он лежит в localStorage ключом на окно. Это
 * личная настройка рабочего места, а не данные проекта, — на сервер не носим.
 */
import { useCallback, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'

export type ResizeEdge = 'left' | 'right' | 'bottom' | 'bottom-left' | 'bottom-right'

export interface Size {
  width: number
  height: number | null
}

export interface SizeLimits {
  minWidth: number
  minHeight: number
  maxWidth: number
  maxHeight: number
  /** 2 у отцентрованного окна, 1 у прижатого к краю. */
  factor: number
}

const STORE_KEY = 'tdms_dialog_size_v1'
/** Зазор до края экрана: окно, растянутое впритык, не поймать за границу. */
const GAP = 32

function clamp(value: number, min: number, max: number) {
  /* max может оказаться меньше min на узком экране — тогда побеждает max,
     иначе окно вылезет за экран ради соблюдения минимума. */
  return Math.min(Math.max(value, min), max)
}

/** Размер после тяги за край `edge` на (dx, dy). Вынесено ради теста. */
export function nextSize(start: Size, edge: ResizeEdge, dx: number, dy: number, lim: SizeLimits): Size {
  const k = lim.factor
  let width = start.width
  if (edge.includes('right')) width = start.width + k * dx
  if (edge.includes('left')) width = start.width - k * dx
  const height = edge.includes('bottom') ? (start.height ?? 0) + k * dy : start.height
  return {
    width: clamp(width, lim.minWidth, lim.maxWidth),
    height: height === null ? null : clamp(height, lim.minHeight, lim.maxHeight),
  }
}

/** Сохранённый размер, приведённый к нынешнему окну. Вынесено ради теста.
 *
 * Ограничение экраном стояло только на время тяги, а восстановление брало из
 * хранилища что записано. Окно, растянутое на 1920, приезжало на ноутбук и не
 * помещалось: у диалога вылезали кнопки, у полки замечаний не оставалось
 * листа. Минимум остаётся минимумом — clamp сам отдаёт предпочтение максимуму,
 * когда экран уже минимальной ширины окна. */
export function fitToWindow(size: Size, minWidth: number, minHeight: number, view: { width: number; height: number }): Size {
  return {
    width: clamp(size.width, minWidth, view.width - GAP),
    height: size.height === null ? null : clamp(size.height, minHeight, view.height - GAP),
  }
}

function readAll(): Record<string, Size> {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw ? (JSON.parse(raw) as Record<string, Size>) : {}
  } catch {
    return {}
  }
}

function remember(key: string, size: Size) {
  try {
    const all = readAll()
    all[key] = size
    localStorage.setItem(STORE_KEY, JSON.stringify(all))
  } catch {
    /* Приватный режим или переполненное хранилище: размер просто не переживёт
       вкладку. Ронять из-за этого открытое окно незачем. */
  }
}

export interface Resizable {
  ref: (node: HTMLElement | null) => void
  width: number
  height: number | null
  edges: ResizeEdge[]
  onDown: (edge: ResizeEdge, e: ReactPointerEvent<HTMLElement>) => void
  onMove: (e: ReactPointerEvent<HTMLElement>) => void
  onUp: (e: ReactPointerEvent<HTMLElement>) => void
}

export function useResizable({
  sizeKey,
  width,
  minWidth = 360,
  minHeight = 200,
  anchor = 'center',
  edges,
}: {
  sizeKey: string
  width: number
  minWidth?: number
  minHeight?: number
  anchor?: 'center' | 'right'
  edges: ResizeEdge[]
}): Resizable {
  const box = useRef<HTMLElement | null>(null)
  const [size, setSize] = useState<Size>(() =>
    fitToWindow(readAll()[sizeKey] ?? { width, height: null }, minWidth, minHeight,
                { width: window.innerWidth, height: window.innerHeight }))
  /* Начало тяги и последний размер держим в ref: обработчик движения живёт до
     конца жеста, и замыкание на состоянии в нём успевает устареть. */
  const drag = useRef<{ edge: ResizeEdge; x: number; y: number; from: Size } | null>(null)
  const latest = useRef(size)

  const onDown = useCallback(
    (edge: ResizeEdge, e: ReactPointerEvent<HTMLElement>) => {
      /* Высота до первой тяги не задана — окно живёт по содержимому. Отсчёт
         берём с фактической: иначе первый же пиксель схлопнет окно до минимума.
         Окну без нижнего края высота не нужна вовсе — панель во весь экран, и
         запоминать её значило бы хранить мусор. */
      const from: Size = {
        width: box.current?.offsetWidth ?? latest.current.width,
        height: edge.includes('bottom') ? (box.current?.offsetHeight ?? 0) : latest.current.height,
      }
      drag.current = { edge, x: e.clientX, y: e.clientY, from }
      e.currentTarget.setPointerCapture(e.pointerId)
      e.preventDefault()
    },
    [],
  )

  const onMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const d = drag.current
      if (!d) return
      const next = nextSize(d.from, d.edge, e.clientX - d.x, e.clientY - d.y, {
        minWidth,
        minHeight,
        maxWidth: window.innerWidth - GAP,
        maxHeight: window.innerHeight - GAP,
        factor: anchor === 'center' ? 2 : 1,
      })
      latest.current = next
      setSize(next)
    },
    [anchor, minWidth, minHeight],
  )

  const onUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!drag.current) return
      drag.current = null
      e.currentTarget.releasePointerCapture(e.pointerId)
      remember(sizeKey, latest.current)
    },
    [sizeKey],
  )

  return {
    ref: (node) => {
      box.current = node
    },
    width: size.width,
    height: size.height,
    edges,
    onDown,
    onMove,
    onUp,
  }
}

/**
 * Боковая рельса с тягой за правый край: список реестров, очередь, справочники.
 *
 * <p>Ширина рельсы — личная настройка рабочего места, как размер окна: длинные
 * коды реестров не читались в фиксированной колонке, а лечится это не общим
 * правилом, а ручкой. Ширина переживает перезагрузку тем же хранилищем, что и
 * размеры окон, ключом на рельсу.
 */
export function ResizableRail({ sizeKey, width, minWidth = 220, style, children }: {
  sizeKey: string
  width: number
  minWidth?: number
  style?: CSSProperties
  children?: ReactNode
}) {
  const box = useResizable({ sizeKey, width, minWidth, anchor: 'right', edges: ['right'] })
  return (
    <div ref={box.ref} style={{ ...style, width: box.width, flex: '0 0 auto', position: 'relative' }}>
      {children}
      <ResizeHandles box={box} />
    </div>
  )
}

/** Полоски по краям окна. Прозрачные: курсор их ловит, глаз не отвлекается. */
export function ResizeHandles({ box }: { box: Resizable }) {
  return (
    <>
      {box.edges.map((edge) => (
        <div
          key={edge}
          role="presentation"
          className={'rsz rsz--' + edge}
          onPointerDown={(e) => box.onDown(edge, e)}
          onPointerMove={box.onMove}
          onPointerUp={box.onUp}
        />
      ))}
    </>
  )
}
